import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services';
import { Affiliate } from './types.ts';

export const useCreateAffiliate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post(`affiliates`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['affiliates'] });
    },
  });
};

// Generic over the select's return type, the way useDomains is: three callers map affiliates to a
// different shape, and a default parameter would pin the result back to Affiliate[].
export const useAffiliates = <T = Affiliate[]>(enabled = true, selectFn?: (data: Affiliate[]) => T) =>
  useQuery({
    queryKey: ['affiliates'],
    queryFn: (): Promise<Affiliate[]> => api.get('affiliates').then((res) => res.data),
    enabled,
    ...(selectFn && { select: selectFn }),
  });

export const useUpdateAffiliate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Affiliate) => api.put(`affiliates/${data.id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['affiliates'] });
    },
  });
};

export const useUpdateAffiliatesBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Affiliate[]) => api.put(`affiliates/bulk`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['affiliates'] });
    },
  });
};

export const useDeleteAffiliate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`affiliates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });
    },
  });
};

// Add useAffiliateTags function to fetch tags by type
export const useAffiliateTags = (tagType: string) =>
  useQuery({
    queryKey: ['tags', tagType],
    queryFn: () => api.get(`/tags/type/${tagType}`).then((res) => res.data),
  });
